import Card from "components/Card";
import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import { PureTooltip } from "components/Tooltip";
import { fmtCurrencyCompact, fmtNumber } from "utils";
import { calculateUsagePercentage, getMonthlyCreditsEquivalent, isEnterprise, isYearlyPlan, getUsageAlertMessage } from "utils/planUtils";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useBillingFlow } from "../providers/BillingFlowProvider";
import { useUser } from "routes/dataroutes/UserData";
import { useNextHigherPlan } from "v2/hooks/billing/pricing-options/useNextHigherPlan";
import { useCounters } from "v2/hooks/billing/useCounters";
import {
  trackBillingV2UpgradeClicked,
  trackBillingV2ContactUsClicked,
  trackBillingV2AnnualToMonthlyClicked,
} from "utils/Tracking";
import UsageAlert from "./UsageAlert";
import { useBillingNavigation } from "../hooks/useBillingNavigation";
import LoadingCard from "../components/LoadingCard";


export default function UsageCard() {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const { counters, loading: countersLoading } = useCounters();
  const { pricingOptions } = usePricingOptions();
  const { nextHigherPlan, isLoading } = useNextHigherPlan();
  const { flowType } = useBillingFlow();
  const { navigate } = useBillingNavigation();

  // activeSubscription is fresher than user.* — Chargebee webhook lags the DB write.
  const currentPlanSlug = activeSubscription?.planSlug;
  const currentCreditLimit = activeSubscription
    ? getMonthlyCreditsEquivalent(activeSubscription)
    : 0;

  const isYearly = isYearlyPlan(currentPlanSlug);
  const hasScheduledChange = Boolean(activeSubscription?.scheduledSubscription);
  const isFreeUser = flowType === "free";
  const shouldContactSales = flowType === "slg";

  // banned_from_free is exempt from the blocked-user lockout.
  const isBlockedNonBanned = user?.isBlocked && user?.blockingCode !== "banned_from_free";

  const autoUpgradeEnabled = pricingOptions?.["auto-upgrade"]?.enabled ?? false;
  const canUsePayg = pricingOptions?.["pay-as-you-go"]?.allowed ?? false;
  const paygEnabled = pricingOptions?.["pay-as-you-go"]?.enabled ?? false;

  const nextCreditLimit = nextHigherPlan ? getMonthlyCreditsEquivalent(nextHigherPlan) : undefined;
  const creditMultiplier = nextCreditLimit && currentCreditLimit
    ? Math.round(nextCreditLimit / currentCreditLimit)
    : null;

  const monthlyCreditsUsed = counters?.requestCount ?? 0;
  const monthlyCreditsLimit = counters?.totalCredits || activeSubscription?.apiCreditLimit || 0;

  const concurrentRequests = counters?.concurrentRequests ?? 0;
  const concurrencyLimit = counters?.concurrencyLimit || activeSubscription?.apiConcurrencyLimit || 0;

  const parsersUsed = counters?.parsersUsed ?? 0;

  // dont need to know the parser limit for enterprise plans
  const parserLimit = isEnterprise(currentPlanSlug)
    ? null
    : activeSubscription?.parserLimit ?? null;

  const payAsYouGoCredits = counters?.payAsYouGoCredits ?? 0;
  const absolutePaygCredits = Math.abs(payAsYouGoCredits);
  const showPaygStacked = canUsePayg && absolutePaygCredits > 0;

  const apiCreditsUsage = calculateUsagePercentage(monthlyCreditsUsed, monthlyCreditsLimit);
  const concurrencyUsage = calculateUsagePercentage(concurrentRequests, concurrencyLimit);
  const alertMessage = getUsageAlertMessage(apiCreditsUsage, concurrencyUsage);
  const isOutOfCredit = apiCreditsUsage >= 1;

  // PAYG users over 100% are covered by overflow — upgrade CTA would mislead.
  const showWarning = alertMessage !== null && !(paygEnabled && isOutOfCredit);

  const parserTooltipContent = currentPlanSlug === "free"
    ? "Upgrade to a paid plan to create and edit AI Parsers."
    : `Each parser you create or edit counts toward your ${parserLimit}-parser limit.`;

  const getUpgradeButtonText = () => {
    if (autoUpgradeEnabled) return "DISABLE AUTO-UPGRADE";
    if (isFreeUser && nextHigherPlan && creditMultiplier) {
      return `GET ${creditMultiplier}X MORE CREDITS FOR JUST ${fmtCurrencyCompact(nextHigherPlan.price / 100, nextHigherPlan.currencyCode)}`;
    }
    if (!nextHigherPlan) return "CONTACT US TO UPGRADE";
    if (isYearly) return "UPGRADE TO A MONTHLY PLAN";
    return `UPGRADE TO ${nextHigherPlan.planName.toUpperCase()}`;
  };
  const upgradeButtonText = getUpgradeButtonText();

  const handleUpgradeClick = () => {
    if (autoUpgradeEnabled) {
      navigate("plans/auto-upgrade/disable", { viewTransition: true });
      return;
    }
    if (!nextHigherPlan) {
      trackBillingV2ContactUsClicked("usage_card_top_tier", currentPlanSlug);
      navigate("plans/contact-sales", { viewTransition: true });
    } else if (isYearly) {
      trackBillingV2AnnualToMonthlyClicked({ source: "usage_card", current_plan: currentPlanSlug });
      navigate("plans", { viewTransition: true });
    } else {
      trackBillingV2UpgradeClicked({
        source: "usage_card",
        current_plan: currentPlanSlug,
        target_plan: nextHigherPlan.planSlug,
      });
      navigate(`plans/upgrade/${nextHigherPlan.planSlug}`);
    }
  };

  const handleContactUsClick = () => {
    trackBillingV2ContactUsClicked("usage_card", currentPlanSlug);
    navigate("plans/contact-sales", { viewTransition: true });
  };

  return (
    <LoadingCard isLoading={isLoading}>
      <Card size="XL" className="h-full">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <h3 className="text-2xl font-semibold text-gray-900">
              Usage this month
            </h3>
            <p className="text-sm text-gray-600">
              Track your consumption and limits
            </p>
          </div>

          {showWarning && (
            <UsageAlert
              severity="warning"
              message={alertMessage}
              onUpgradeClick={handleUpgradeClick}
            />
          )}

          <div className="flex flex-col gap-4">
            <ProgressBar
              label="API Credits"
              current={monthlyCreditsUsed}
              max={monthlyCreditsLimit}
              fullWidth
              valueSuffix="used"
              loading={countersLoading}
              stackedValue={showPaygStacked ? absolutePaygCredits : undefined}
              stackedTooltip={showPaygStacked
                ? `Pay-as-you-go overage: ${absolutePaygCredits.toLocaleString()} credits`
                : undefined}
            />

            <ProgressBar
              label="Concurrent threads"
              current={concurrentRequests}
              max={concurrencyLimit}
              fullWidth
              valueSuffix="active"
              loading={countersLoading}
            />

            {parserLimit === null ? (
              <PureTooltip content="Each parser create or edit costs 30,000 credits. Your plan includes unlimited parsers.">
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm font-medium text-neutral-900">AI Parsers</span>
                  <span className="text-sm text-neutral-900">{fmtNumber(parsersUsed)} active</span>
                </div>
              </PureTooltip>
            ) : (
              <PureTooltip content={parserTooltipContent}>
                <ProgressBar
                  label="AI Parsers"
                  current={parsersUsed}
                  max={parserLimit}
                  fullWidth
                  valueSuffix="active"
                  loading={countersLoading}
                />
              </PureTooltip>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {shouldContactSales ? (
            <Button
              text="CONTACT US TO UPGRADE"
              className="button button-primary"
              size="SM"
              onClick={handleContactUsClick}
            />
          ) : (
            <Button
              text={isYearly && hasScheduledChange ? "CHANGE SCHEDULED" : upgradeButtonText}
              className="button button-primary"
              size="SM"
              onClick={handleUpgradeClick}
              disabled={isBlockedNonBanned || (isYearly && hasScheduledChange)}
            />
          )}
          {!isFreeUser && !shouldContactSales && (
            <Button
              text="VIEW ALL PLANS"
              href="plans"
              className="button button-secondary"
              size="SM"
              disabled={isBlockedNonBanned}
            />
          )}
        </div>
      </Card>
    </LoadingCard>
  );
}
